import {
  CreateLoadBuildingSuggestionRequest,
  CreateSharepointLoadBuildingSuggestionRequest,
} from 'lib/api/createLoadBuildingSuggestion';
import { CreateQuoteRequestSuggestionRequest } from 'lib/api/createQuoteRequestSuggestion';
import {
  SuggestionCategories,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import {
  LoadBuildingSuggestions,
  SuggestedLoad,
} from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import {
  QuoteChanges,
  SuggestedQuoteChange,
} from 'types/suggestions/QuoteSuggestions';

type SuggestedQuoteWithOptionalCustomer = QuoteChanges & {
  customer?: {
    name?: string;
  };
};

const getSuggestedCustomerName = (
  suggested: SuggestedQuoteChange['suggested']
): string => {
  const withOptionalCustomer = suggested as SuggestedQuoteWithOptionalCustomer;
  return withOptionalCustomer.customer?.name?.trim() ?? '';
};

export const withSuggestedCustomerName = (
  suggestion: SuggestedQuoteChange,
  customerName: string
): SuggestedQuoteChange => {
  const trimmedName = customerName.trim();
  if (!trimmedName) {
    return suggestion;
  }
  if (getSuggestedCustomerName(suggestion.suggested)) {
    return suggestion;
  }
  if (suggestion.suggested.customerExternalTMSID) {
    return suggestion;
  }

  return {
    ...suggestion,
    suggested: {
      ...(suggestion.suggested as SuggestedQuoteWithOptionalCustomer),
      customer: { name: trimmedName },
    } as SuggestedQuoteChange['suggested'],
  };
};

export const buildLocalQuoteSuggestionFromRequest = (
  requestData: CreateQuoteRequestSuggestionRequest,
  sourceCustomerName: string
): SuggestedQuoteChange => {
  const { portalCustomerName: _portalCustomerName, ...suggestedData } =
    requestData;
  const fallbackCustomerName =
    suggestedData.customer?.name || sourceCustomerName || '';

  return {
    id: 0,
    createdAt: new Date().toISOString(),
    account: '',
    status: SuggestionStatus.Pending,
    pipeline: SuggestionPipelines.QuickQuote,
    category: SuggestionCategories.QuickQuote,
    attachment: null,
    emailID: 0,
    latestEmailIDFromThread: 0,
    source: requestData.source,
    sourceExternalID: requestData.sourceExternalID,
    sourceURL: requestData.sourceURL,
    sourceCategory: requestData.sourceCategory,
    suggested: {
      ...suggestedData,
      stops: suggestedData.stops ?? [],
      customer: {
        name: fallbackCustomerName,
      },
      pickupDate: suggestedData.pickupDate
        ? new Date(suggestedData.pickupDate)
        : null,
      deliveryDate: suggestedData.deliveryDate
        ? new Date(suggestedData.deliveryDate)
        : null,
    } as SuggestedQuoteChange['suggested'],
  };
};

export const buildLocalLoadBuildingSuggestionFromRequest = (
  requestData:
    | CreateLoadBuildingSuggestionRequest
    | CreateSharepointLoadBuildingSuggestionRequest,
  suggestionID: number,
  resolvedSuggested?: SuggestedLoad
): LoadBuildingSuggestions => {
  const suggested = resolvedSuggested || requestData.suggested;
  const suggestedBillTo = suggested?.billTo;
  const suggestedCustomer = suggested?.customer;
  const billToExternalTMSID = suggestedBillTo?.externalTMSID || '';
  const customerExternalTMSID = suggestedCustomer?.externalTMSID || '';
  const isBillToDistinct =
    !!suggestedBillTo &&
    !!suggestedCustomer &&
    !!billToExternalTMSID &&
    billToExternalTMSID !== customerExternalTMSID;

  return {
    id: suggestionID,
    createdAt: new Date().toISOString(),
    account: '',
    status: SuggestionStatus.Pending,
    pipeline: SuggestionPipelines.LoadBuilding,
    category: SuggestionCategories.LoadBuilding,
    isBillToDistinct,
    attachment: null,
    emailID: 0,
    latestEmailIDFromThread: 0,
    source: requestData.source,
    sourceExternalID: requestData.sourceExternalID,
    sourceURL: requestData.sourceURL,
    sourceCategory: requestData.sourceCategory,
    suggested,
    applied: {} as LoadBuildingSuggestions['applied'],
  };
};
